import React, { useMemo, useRef, useEffect } from 'react';
import { arrayOf, bool, object, string, func, oneOf, shape, number, node } from 'prop-types';
import { noop } from 'lodash';
import classnames from 'classnames';
import { I18n } from '@fiverr-private/i18n-react';
import { Popover, Icon } from '@fiverr-private/fit';
import { l_times } from '@fiverr-private/icons';
import {
    getDisplayedNotableClients,
    HIDDEN_CLIENTS_MODE,
    normalizeOrigin
} from '../../../utils/helpers/notableClients';
import CompactClientList from '../CompactClientList';
import NotableClientsAboutClosable from '../NotableClientsAboutClosable';
import {
    reportNotableClientsShown,
    reportNotableClientsHovered,
    trackUnknownView,
    trackUnknownInteraction,
    trackViewByOrigin,
    trackInteractionByOrigin
} from '../../../utils/reports';
import { count } from '../../../utils/stats/client';
import notableClientType from '../../../utils/types/notableClient';

import './style.scss';
import ClientLogo from '../ClientLogo';

const TRIGGERS = {
    CLICK: 'click_mode',
    HOVER: 'hover_mode'
};
const ICON_SIZE = 12;
const POPOVER_DELAY = 300;
const MAX_INNER_WIDTH_BUFFER = 300;

const adaptPopoverPositionToContainer = (component) => {
    let position;

    if (component) {
        const domRect = component.getBoundingClientRect();

        if (domRect?.right &&
            window?.innerWidth &&
            domRect.right + MAX_INNER_WIDTH_BUFFER > window.innerWidth) {
            position = Popover.POSITIONS.BOTTOM_RIGHT;
        }
    }

    return position;
};

const NotableClientsCompactBar = ({
    seller = {},
    origin = '',
    className = '',
    labelClassName = '',
    logoClassName = '',
    boxContentClassName = '',
    title,
    onClick = noop,
    onHover = noop,
    hiddenClientsMode,
    showCompanyNames,
    trigger = TRIGGERS.HOVER,
    logoSize,
    popoverPosition,
    notableClients,
    limitClients,
    gigId,
    containerRef
}) => {
    const popoverRef = useRef();
    const displayClients = useMemo(() => getDisplayedNotableClients(notableClients), [notableClients]);
    const normalizedOrigin = useMemo(() => normalizeOrigin(origin), [origin]);

    const isHover = trigger === TRIGGERS.HOVER;
    const isClick = trigger === TRIGGERS.CLICK;
    const delay = isHover ? POPOVER_DELAY : undefined;
    const popoverTrigger = isClick ? Popover.TRIGGERS.CLICK : Popover.TRIGGERS.HOVER;
    const compactClientsListProps = {
        displayClients,
        hiddenClientsMode,
        showCompanyNames,
        logoSize,
        logoClassName,
        limit: limitClients
    };

    useEffect(() => {
        count(`notable_clients_compact_bar_render.${normalizedOrigin}`);
        reportNotableClientsShown({
            sellerName: seller?.name,
            origin: normalizedOrigin,
            allClients: displayClients,
            gigId
        });
        trackViewByOrigin({
            seller,
            origin: normalizedOrigin,
            allClients: displayClients
        }, trackUnknownView);
    });

    const onHoverHandler = () => {
        onHover(true);

        if (isHover) {
            reportNotableClientsHovered({
                origin: normalizedOrigin,
                allClients: displayClients,
                gigId,
                sellerName: seller?.name
            });
            trackInteractionByOrigin({
                seller,
                origin: normalizedOrigin,
                allClients: displayClients
            }, trackUnknownInteraction);
        }
    };

    const pickedPopoverPosition = useMemo(() => (
        popoverPosition || adaptPopoverPositionToContainer(containerRef) || Popover.POSITIONS.BOTTOM_LEFT
    ), [popoverPosition, containerRef]);

    return (
        <Popover ref={popoverRef} className={classnames('notable-clients-compact-bar-popover')}
            contentClassName="notable-clients-compact-bar-content"
            boxContentClassName={classnames('compact-bar-popover-box-content', boxContentClassName)}
            containerClassName="notable-clients-compact-bar-container nc-popover-zindex"
            trigger={popoverTrigger}
            position={pickedPopoverPosition}
            delay={delay}
            content={
                <>
                    {isClick &&
                        <Icon size={ICON_SIZE} className="close-button" onClick={() => { popoverRef.current?.close?.(); }}>
                            {l_times}
                        </Icon>
                    }
                    <NotableClientsAboutClosable title={title} notableClients={notableClients}
                        className={'compact-bar-about-container'}
                        logoClassName={logoClassName}/>
                </>
            }>
            <span className={classnames('notable-clients-compact-bar', { 'bg-hover': isClick }, className)}
                onClick={onClick}
                onMouseEnter={onHoverHandler}
                onMouseLeave={() => { onHover(false); }}>
                <p className={classnames(
                    'bar-label',
                    labelClassName,
                    {
                        'tbody-7': isHover,
                        'text-semi-bold': isHover,
                        'tbody-6': isClick
                    })}>
                    <I18n k={`notable_clients.compact_bar.${trigger}.label`}/>
                </p>
                <span className="compact-list-wrapper">
                    <CompactClientList {...compactClientsListProps}/>
                </span>
            </span>
        </Popover>
    );
};

NotableClientsCompactBar.TRIGGERS = TRIGGERS;
NotableClientsCompactBar.HIDDEN_CLIENTS_MODE = HIDDEN_CLIENTS_MODE;

NotableClientsCompactBar.propTypes = {
    seller: shape({
        name: string
    }),
    origin: string,
    className: string,
    boxContentClassName: string,
    labelClassName: string,
    logoClassName: string,
    title: node,
    onClick: func,
    logoSize: oneOf(Object.values(ClientLogo.SIZES)),
    onHover: func,
    hiddenClientsMode: oneOf(Object.values(HIDDEN_CLIENTS_MODE)),
    showCompanyNames: bool,
    trigger: oneOf(Object.values(TRIGGERS)),
    popoverPosition: oneOf(Object.values(Popover.POSITIONS)),
    notableClients: arrayOf(notableClientType).isRequired,
    limitClients: number,
    gigId: number,
    containerRef: object
};

export default NotableClientsCompactBar;
